import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ACTIVE_ORG_KEY = "active_org_id";

function formatError(detail) {
  if (!detail) return "Something went wrong.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// Axios auth + active-org plumbing. Both live on shared default headers so
// every request — including those made outside React render — picks them up.
function setupAxiosAuth(token) {
  if (token) {
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
  } else {
    delete axios.defaults.headers.common["Authorization"];
  }
}

function setupAxiosActiveOrg(orgId) {
  if (orgId) {
    axios.defaults.headers.common["X-Active-Org"] = orgId;
  } else {
    delete axios.defaults.headers.common["X-Active-Org"];
  }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("auth_token"));
  const [loading, setLoading] = useState(true);
  // F2: active_org_id selected by a super_admin. null = "union view" / home.
  // Persisted in localStorage so the choice survives a page refresh.
  const [activeOrgId, setActiveOrgIdState] = useState(() => localStorage.getItem(ACTIVE_ORG_KEY) || null);

  const setActiveOrgId = useCallback(async (orgId) => {
    if (orgId) {
      localStorage.setItem(ACTIVE_ORG_KEY, orgId);
    } else {
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
    setupAxiosActiveOrg(orgId);
    setActiveOrgIdState(orgId);
    // Re-fetch /auth/me so the user dict reflects the new active_org context.
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch { /* ignore */ }
    // Hard refresh the page so every child component re-fetches fresh
    // org-scoped data. This is the simplest, safest invalidation strategy —
    // no risk of one stale list lingering from before the switch.
    if (typeof window !== "undefined") {
      window.location.reload();
    }
  }, []);

  const checkAuth = useCallback(async () => {
    const stored = localStorage.getItem("auth_token");
    if (!stored) {
      setUser(false);
      setToken(null);
      setLoading(false);
      return;
    }
    setupAxiosAuth(stored);
    setupAxiosActiveOrg(activeOrgId);
    setToken(stored);
    try {
      const res = await axios.get(`${API}/auth/me`);
      setUser(res.data);
    } catch {
      localStorage.removeItem("auth_token");
      setupAxiosAuth(null);
      setupAxiosActiveOrg(null);
      setToken(null);
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, [activeOrgId]);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      const { token: jwt, user: userData } = res.data;
      localStorage.setItem("auth_token", jwt);
      setupAxiosAuth(jwt);
      // Login always resets to the user's home org. A super_admin can re-pick later.
      localStorage.removeItem(ACTIVE_ORG_KEY);
      setupAxiosActiveOrg(null);
      setActiveOrgIdState(null);
      setToken(jwt);
      setUser(userData);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const register = async (email, password, name) => {
    try {
      const res = await axios.post(`${API}/auth/register`, { email, password, name });
      const { token: jwt, user: userData } = res.data;
      localStorage.setItem("auth_token", jwt);
      setupAxiosAuth(jwt);
      setToken(jwt);
      setUser(userData);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  const logout = async () => {
    try { await axios.post(`${API}/auth/logout`); } catch { /* ignore */ }
    localStorage.removeItem("auth_token");
    localStorage.removeItem(ACTIVE_ORG_KEY);
    setupAxiosAuth(null);
    setupAxiosActiveOrg(null);
    setToken(null);
    setActiveOrgIdState(null);
    setUser(false);
  };

  const changePassword = async (currentPassword, newPassword) => {
    try {
      await axios.post(`${API}/auth/change-password`, {
        current_password: currentPassword,
        new_password: newPassword,
      });
      // Refresh user so must_change_password flips to false everywhere.
      const me = await axios.get(`${API}/auth/me`);
      setUser(me.data);
      return { success: true };
    } catch (e) {
      return { success: false, error: formatError(e.response?.data?.detail) };
    }
  };

  return (
    <AuthContext.Provider value={{
      user, token, loading, login, register, logout, changePassword,
      activeOrgId, setActiveOrgId,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
